import { useState } from 'react'
import {
  BiPlug,
  BiCheck,
  BiX,
  BiCog,
  BiEnvelope,
  BiGroup,
  BiCalendar,
  BiBarChart
} from 'react-icons/bi'
import Alert from '../components/Alert'

interface Integration {
  id: string
  name: string
  description: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  category: 'email' | 'crm' | 'calendar' | 'analytics'
  isConnected: boolean
  isPopular?: boolean
  features: string[]
}

const Integrations = () => {
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'email' | 'crm' | 'calendar' | 'analytics'>('all')

  const integrations: Integration[] = [
    {
      id: 'gmail',
      name: 'Gmail',
      description: 'Connect your Gmail account to send billing notifications and invoices',
      icon: BiEnvelope,
      category: 'email',
      isConnected: true,
      isPopular: true,
      features: ['Send emails', 'Track opens', 'Track clicks', 'Auto-sync contacts']
    },
    {
      id: 'outlook',
      name: 'Microsoft Outlook',
      description: 'Integrate with Outlook for seamless billing communications',
      icon: BiEnvelope,
      category: 'email',
      isConnected: false,
      isPopular: true,
      features: ['Send emails', 'Track opens', 'Track clicks', 'Calendar sync']
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      description: 'Sync customers and billing data with your Salesforce CRM',
      icon: BiGroup,
      category: 'crm',
      isConnected: false,
      isPopular: true,
      features: ['Sync contacts', 'Update customer status', 'Track billing activities', 'Custom fields']
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      description: 'Connect with HubSpot CRM for comprehensive customer management',
      icon: BiGroup,
      category: 'crm',
      isConnected: true,
      features: ['Contact sync', 'Subscription tracking', 'Billing activity logging', 'Payment pipeline management']
    },
    {
      id: 'calendly',
      name: 'Calendly',
      description: 'Integrate meeting scheduling for billing consultations and support',
      icon: BiCalendar,
      category: 'calendar',
      isConnected: false,
      features: ['Schedule meetings', 'Auto-book calls', 'Calendar sync', 'Meeting reminders']
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics',
      description: 'Track website visits from your billing portal and payment pages',
      icon: BiBarChart,
      category: 'analytics',
      isConnected: false,
      features: ['Track payment conversions', 'Portal analytics', 'Billing attribution', 'Revenue tracking']
    }
  ]

  const categories = [
    { id: 'all', name: 'All Integrations', icon: BiPlug },
    { id: 'email', name: 'Email', icon: BiEnvelope },
    { id: 'crm', name: 'CRM', icon: BiGroup },
    { id: 'calendar', name: 'Calendar', icon: BiCalendar },
    { id: 'analytics', name: 'Analytics', icon: BiBarChart }
  ]

  const filteredIntegrations = selectedCategory === 'all'
    ? integrations
    : integrations.filter(integration => integration.category === selectedCategory)

  const handleConnect = async (integrationId: string) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))

      setAlert({
        type: 'success',
        message: `Successfully connected to ${integrations.find(i => i.id === integrationId)?.name}!`
      })

      // In a real app, you would update the integration status
      console.log(`Connecting to ${integrationId}`)
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to connect integration. Please try again.'
      })
    }
  }

  const handleDisconnect = async (integrationId: string) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))

      setAlert({
        type: 'info',
        message: `Disconnected from ${integrations.find(i => i.id === integrationId)?.name}`
      })

      // In a real app, you would update the integration status
      console.log(`Disconnecting from ${integrationId}`)
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to disconnect integration. Please try again.'
      })
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Alert */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          Integrations
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          Connect your favorite tools to supercharge your billing management
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const IconComponent = category.icon
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === category.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
            >
              <IconComponent size={16} />
              <span>{category.name}</span>
            </button>
          )
        })}
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => {
          const IconComponent = integration.icon
          return (
            <div
              key={integration.id}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 relative"
            >
              {/* Popular Badge */}
              {integration.isPopular && (
                <div className="absolute top-4 right-4">
                  <span className="bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 text-xs font-medium px-2 py-1 rounded-full">
                    Popular
                  </span>
                </div>
              )}

              {/* Integration Header */}
              <div className="flex items-start space-x-4 mb-4">
                <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                  <IconComponent size={24} className="text-slate-600 dark:text-slate-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {integration.name}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {integration.description}
                  </p>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                  Features:
                </h4>
                <ul className="space-y-1">
                  {integration.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                      <BiCheck size={16} className="text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {integration.isConnected ? (
                    <>
                      <BiCheck size={16} className="text-green-500" />
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">
                        Connected
                      </span>
                    </>
                  ) : (
                    <>
                      <BiX size={16} className="text-slate-400" />
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        Not connected
                      </span>
                    </>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {integration.isConnected && (
                    <button
                      onClick={() => handleDisconnect(integration.id)}
                      className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                      title="Configure"
                    >
                      <BiCog size={16} />
                    </button>
                  )}

                  <button
                    onClick={() => integration.isConnected ? handleDisconnect(integration.id) : handleConnect(integration.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${integration.isConnected
                      ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30'
                      : 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600'
                      }`}
                  >
                    {integration.isConnected ? 'Disconnect' : 'Connect'}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {filteredIntegrations.length === 0 && (
        <div className="text-center py-12">
          <BiPlug size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            No integrations found
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            Try selecting a different category or check back later for new integrations.
          </p>
        </div>
      )}
    </div>
  )
}

export default Integrations 